/**
 * Safety Banner Component — PRD Section 9.1
 *
 * Non-dismissible, always-present visual element for Brain/Hybrid modes.
 * Renders at standard/elevated/critical levels based on SafetyBannerConfig.
 *
 * A critical banner offers help (owner, 21 Oct 2026: D2): "Get help" opens a
 * dialog with the crisis lines the server chose for the person's country, each
 * one a call away (`tel:`), and — where the portal has it — the way to Care
 * support. It used to be a link to `/support`, a page no portal has.
 */

import { crisisLineHref, useCrisisLineWords } from '@tonnie/copilot-core';
import { ChevronRight, Heart, LifeBuoy, Phone } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import type { SafetyBannerConfig, SafetyBannerCrisisHelp } from '@tonnie/types';
import {
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogMedia,
  DialogTitle,
} from '@tonnie/ui';
import { useState } from 'react';

interface SafetyBannerProps {
  config: SafetyBannerConfig | null;
  /**
   * Where this portal's Care support lives (`/care` on Citizen Web). Omitted
   * on a portal with no Care page — the lines are offered all the same.
   */
  careHref?: string;
  className?: string;
}

const LEVEL_STYLES: Record<SafetyBannerConfig['level'], string> = {
  standard: 'bg-muted/50 text-muted-foreground border-muted',
  elevated: 'bg-tone-amber-subtle text-tone-amber border-tone-amber-line',
  critical: 'bg-tone-red-subtle text-tone-red border-tone-red-line',
};

const LEVEL_ICONS: Record<SafetyBannerConfig['level'], string> = {
  standard: 'ℹ',
  elevated: '⚠',
  critical: '⛔',
};

/** The banner carries `ns:key` references (buildSafetyBanner); i18next reads `ns.key`. */
const i18nKey = (ref: string) => ref.replace(':', '.');

export function SafetyBanner({ config, careHref, className }: SafetyBannerProps) {
  const { t } = useTranslation();
  const [helpOpen, setHelpOpen] = useState(false);

  if (!config || !config.visible) return null;

  return (
    <>
      <div
        className={cn(
          'px-3 py-1.5 text-2xs leading-tight border-b select-none flex items-center gap-1.5',
          LEVEL_STYLES[config.level],
          className
        )}
        role="status"
        aria-live="polite"
        data-testid="copilot-safety-banner"
        data-level={config.level}
      >
        <span aria-hidden="true">{LEVEL_ICONS[config.level]}</span>
        <span className="flex-1">{t(i18nKey(config.message))}</span>
        {config.escalationAction && (
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            className="underline hover:no-underline font-medium whitespace-nowrap"
            data-testid="copilot-safety-get-help"
          >
            {t(i18nKey(config.escalationAction.label))}
          </button>
        )}
      </div>
      {config.escalationAction && (
        <CrisisHelpDialog
          open={helpOpen}
          onOpenChange={setHelpOpen}
          help={config.escalationAction}
          careHref={careHref}
        />
      )}
    </>
  );
}

/** The help: each line, dialable, and the way to Care support. */
function CrisisHelpDialog({
  open,
  onOpenChange,
  help,
  careHref,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  help: SafetyBannerCrisisHelp;
  careHref?: string;
}) {
  const { t } = useTranslation();
  const wordsOf = useCrisisLineWords();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        layout="scroll"
        size="sm"
        closeLabel={t('common.close')}
        data-testid="copilot-crisis-help"
      >
        <DialogHeader variant="bar" media={<DialogMedia icon={LifeBuoy} intent="danger" />}>
          <DialogTitle>{t('copilot.safety.help.title')}</DialogTitle>
          <DialogDescription>{t('copilot.safety.help.intro')}</DialogDescription>
        </DialogHeader>
        <DialogBody variant="bar" className="space-y-3">
          <ul className="space-y-3">
            {help.lines.map((line) => {
              const words = wordsOf(line);
              return (
                <li key={line.id}>
                  <a
                    href={crisisLineHref(line)}
                    className="border-border hover:bg-muted/50 focus-visible:ring-ring flex items-center justify-between gap-4 rounded-lg border px-4 py-3 focus-visible:ring-2 focus-visible:outline-none"
                    data-testid={`copilot-crisis-line-${line.id}`}
                  >
                    <span className="min-w-0 space-y-0.5">
                      <span className="text-foreground block text-sm font-semibold">
                        {words.name}
                      </span>
                      <span className="text-muted-foreground block text-xs">
                        {words.availability}
                      </span>
                    </span>
                    <span className="text-tone-red flex shrink-0 items-center gap-1.5 text-base font-semibold tabular-nums">
                      {line.display}
                      <Phone className="size-4" aria-hidden="true" />
                    </span>
                  </a>
                </li>
              );
            })}
          </ul>
          {careHref && (
            <a
              href={careHref}
              className="border-border hover:bg-muted/50 focus-visible:ring-ring flex items-center justify-between gap-4 rounded-lg border px-4 py-3 focus-visible:ring-2 focus-visible:outline-none"
              data-testid="copilot-crisis-care"
            >
              <span className="min-w-0 space-y-0.5">
                <span className="text-foreground flex items-center gap-2 text-sm font-semibold">
                  <Heart className="size-4" aria-hidden="true" />
                  {t('copilot.safety.help.care')}
                </span>
                <span className="text-muted-foreground block text-xs">
                  {t('copilot.safety.help.careHint')}
                </span>
              </span>
              <ChevronRight className="text-muted-foreground size-4 shrink-0" aria-hidden="true" />
            </a>
          )}
        </DialogBody>
        <DialogFooter
          variant="bar"
          start={
            <Button type="button" variant="ghost" asChild>
              <DialogClose data-testid="copilot-crisis-help-close">{t('common.close')}</DialogClose>
            </Button>
          }
        />
      </DialogContent>
    </Dialog>
  );
}
